// library imports
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { useParams, useNavigate, useLocation } from 'react-router-dom';

// store imports
import { useDomainStore, useFamilyStore } from '@/hooks/useStores';

// component imports
import { PersonCard } from '@/components/person/PersonCard';
import { PetCard } from '@/components/pet/PetCard';
import FamilyMembersFilterBar, { type FilterType } from '@/components/family/FamilyMembersFilterBar';
import MembersResultsBar from '@/components/family/MembersResultsBar';
import MembersSearchSortRow from '@/components/family/MembersSearchSortRow';
import FamilyPageHeader from '@/components/layout/FamilyPageHeader';
import EditFamilyNameModal from '@/components/family/EditFamilyNameModal';
import ScreenHelpDot from '@/components/help/ScreenHelpDot';
import ScreenTourLayer from '@/components/help/ScreenTourLayer';
import { useScreenTour } from '@/hooks/useScreenTour';
import {
  FAMILY_MEMBERS_TOUR_STEPS,
  FAMILY_MEMBERS_TOUR_STORAGE_KEY,
  resolveFamilyMembersTourSteps
} from '@/data/family-members-tour';
import Spinner from '@/components/shared/Spinner';
import { canInvite, canModifyFamilyMembers } from '@/utils/rolePermissions';
import {
  DEFAULT_MEMBERS_PEOPLE_FILTERS,
  defaultSortDirection,
  type MembersSortBy,
  type MembersSortDirection
} from '@/types/members-list';
import { matchesPeopleFilters, sortPeople } from '@/utils/membersList';

// styles
import './family-members-screen.css';

const FamilyMembersScreen: React.FC = observer(() => {
  const { t } = useTranslation('screens');
  const domainStore = useDomainStore();
  const familyStore = useFamilyStore();
  const navigate = useNavigate();
  const location = useLocation();
  const { familyId: familyIdParam } = useParams<{
    familyId: string;
  }>();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [filter, setFilter] = useState<FilterType>('people');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [sortBy, setSortBy] = useState<MembersSortBy>('last-first');
  const [sortDirection, setSortDirection] = useState<MembersSortDirection>(() => defaultSortDirection('last-first'));
  const [peopleFilters, setPeopleFilters] = useState(DEFAULT_MEMBERS_PEOPLE_FILTERS);
  const [showEditNameModal, setShowEditNameModal] = useState(false);
  const [canAddByMemberId, setCanAddByMemberId] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const loadFamilyData = async () => {
      setErrorMessage(null);

      if (!familyIdParam) {
        setErrorMessage(t('errors.noFamily'));
        return;
      }

      try {
        domainStore.setSelectedFamily(familyIdParam);
        if (!familyStore.isLoaded || familyStore.familyId !== familyIdParam) {
          await familyStore.loadFamilyData(familyIdParam);
        }

        if (familyStore.people.size === 0 && familyStore.pets.size === 0) {
          setErrorMessage(t('members.noMembersInFamily'));
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Failed to load family data:', error);
        setErrorMessage(t('members.loadFailed'));
      }
    };

    loadFamilyData();
  }, [domainStore, familyStore, familyIdParam, t]);

  const addFlowState = {
    from: 'members' as const,
    returnTo: `${location.pathname}${location.search}`
  };

  const handleAddClick = (parentFamilyMemberId: string, option: 'child' | 'spouse' | 'pet') => {
    if (!familyIdParam) return;
    if (option === 'pet') {
      navigate(`/families/${familyIdParam}/pets/add?parentId=${parentFamilyMemberId}`, { state: addFlowState });
    } else {
      navigate(`/families/${familyIdParam}/people/add?parentId=${parentFamilyMemberId}&type=${option}`, {
        state: addFlowState
      });
    }
  };

  const handleTreeViewClick = (clickedFamilyMemberId: string) => {
    if (familyIdParam) {
      navigate(`/families/${familyIdParam}/hybrid-pedigree/${clickedFamilyMemberId}`);
    }
  };

  const handleProfileClick = (clickedFamilyMemberId: string) => {
    if (familyIdParam) {
      navigate(`/families/${familyIdParam}/people/${clickedFamilyMemberId}`, {
        state: { from: 'members' }
      });
    }
  };

  const handlePetProfileClick = (petFamilyMemberId: string) => {
    if (familyIdParam) {
      navigate(`/families/${familyIdParam}/pets/${petFamilyMemberId}`, { state: { from: 'members' } });
    }
  };

  const handleEditNameSuccess = (displayName: string, alternativeName: string | null) => {
    if (!familyIdParam || !domainStore.currentFamily) return;
    domainStore.updateFamilyMetadata({
      id: familyIdParam,
      name: domainStore.currentFamily.name,
      displayName,
      alternativeName,
      memberCount: domainStore.currentFamily.memberCount,
      createdAt: domainStore.currentFamily.createdAt,
      updatedAt: domainStore.currentFamily.updatedAt,
      createdBy: domainStore.currentFamily.createdBy ?? null,
      createdByNickname: domainStore.currentFamily.createdByNickname ?? null,
      updatedBy: domainStore.currentFamily.updatedBy ?? null,
      updatedByNickname: domainStore.currentFamily.updatedByNickname ?? null,
      userRole: domainStore.currentFamily.userRole ?? null
    });
  };

  // Derive from the store on each render — MobX tracks observables and only re-renders when
  // people/pets change. Do not cache PersonModel instances in useMemo (map ref is stable).
  const allPeople = Array.from(familyStore.people.values()).filter((person) => !person.isPlaceholder);
  const allPets = Array.from(familyStore.pets.values());

  let filteredMemberIds: string[] = [];
  if (filter !== 'pets') {
    let people = allPeople.filter((person) =>
      matchesPeopleFilters(person, peopleFilters, (id) => familyStore.memberHasRelationships(id))
    );
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      people = people.filter((person) => {
        const nameMatch = person.fullName.toLowerCase().includes(query);
        const placeMatch = person.birthPlace?.trim().toLowerCase().includes(query) ?? false;
        return nameMatch || placeMatch;
      });
    }
    filteredMemberIds = sortPeople(people, sortBy, sortDirection).map((person) => person.familyMemberId);
  }

  let filteredPetIds: string[] = [];
  if (filter !== 'people') {
    let list = allPets;
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      list = list.filter(
        (p) =>
          p.displayName.toLowerCase().includes(query) ||
          (p.subType && p.subType.toLowerCase().includes(query)) ||
          p.category.toLowerCase().includes(query)
      );
    }
    filteredPetIds = [...list].sort((a, b) => a.displayName.localeCompare(b.displayName)).map((p) => p.familyMemberId);
  }

  const filteredIdsKey = filteredMemberIds.slice().sort().join(',');

  useEffect(() => {
    if (filter === 'pets') return;
    const ids = filteredIdsKey ? filteredIdsKey.split(',') : [];
    if (ids.length === 0) return;
    let cancelled = false;
    const loadCanAdd = async () => {
      const results = await canModifyFamilyMembers(ids);
      if (!cancelled) {
        setCanAddByMemberId((prev) => ({ ...prev, ...results }));
      }
    };
    loadCanAdd();
    return () => {
      cancelled = true;
    };
  }, [filter, filteredIdsKey]);

  const familyDisplayName =
    domainStore.currentFamily?.displayName || domainStore.currentFamily?.name || t('members.familyMembersFallback');
  const canInviteMembers = canInvite(domainStore.currentFamily?.userRole, domainStore.currentFamily?.plan);

  const totalResults = filteredMemberIds.length + filteredPetIds.length;
  const isLoading = !familyStore.isLoaded && familyStore.isLoading;

  const handleInviteMembers = () => {
    if (!familyIdParam) return;
    navigate(`/families/${familyIdParam}/invite`);
  };

  const membersTour = useScreenTour({
    steps: FAMILY_MEMBERS_TOUR_STEPS,
    completedStorageKey: FAMILY_MEMBERS_TOUR_STORAGE_KEY,
    resolveSteps: (steps) => resolveFamilyMembersTourSteps(steps, totalResults > 0, canInviteMembers)
  });

  if (errorMessage) {
    return (
      <div className="members-page members-cards-scope">
        <div className="members-error" role="alert">
          <h3 className="members-error__title">{errorMessage}</h3>
        </div>
      </div>
    );
  }

  return (
    <div className="members-page members-cards-scope">
      <FamilyPageHeader
        rootTourId="members-intro"
        familyDisplayName={familyDisplayName}
        sectionTitle={t('members.title')}
        onEditFamilyName={() => setShowEditNameModal(true)}
        onStartTour={membersTour.startTour}
        intro={
          <>
            {t('members.lede')} <ScreenHelpDot />
          </>
        }
        introTrailing={
          canInviteMembers ? (
            <button type="button" className="btn btn--primary" data-tour="members-invite" onClick={handleInviteMembers}>
              {t('members.invite')}
            </button>
          ) : undefined
        }
      />

      <div data-tour="members-filters">
        <FamilyMembersFilterBar
          currentFilter={filter}
          onFilterChange={setFilter}
          peopleCount={allPeople.length}
          petsCount={allPets.length}
          peopleFilters={peopleFilters}
          onPeopleFiltersChange={setPeopleFilters}
        />
        <MembersSearchSortRow
          filter={filter}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          sortBy={sortBy}
          sortDirection={sortDirection}
          onSortByChange={setSortBy}
          onSortDirectionChange={setSortDirection}
        />
      </div>

      {!isLoading && <MembersResultsBar totalResults={totalResults} filter={filter} searchQuery={searchQuery} />}

      {isLoading ? (
        <div className="members-loading">
          <Spinner message={t('members.loading')} />
        </div>
      ) : totalResults === 0 ? (
        <div className="members-empty">
          <p className="members-empty__title">
            {filter === 'pets' && !searchQuery.trim()
              ? t('members.noPetsYet')
              : searchQuery.trim()
                ? t('members.noSearchMatch', { query: searchQuery.trim() })
                : filter !== 'pets' &&
                    (peopleFilters.living !== 'all' || peopleFilters.hasPhoto || peopleFilters.hasNoRelationships)
                  ? t('filters.noMembersMatch')
                  : t('members.noMembersFound')}
          </p>
          <p className="members-empty__body">
            {filter === 'pets' && !searchQuery.trim() ? t('members.noPetsHint') : t('filters.tryDifferentSearch')}
          </p>
        </div>
      ) : (
        <>
          {filter === 'all' && filteredMemberIds.length > 0 && (
            <h2 className="members-section-heading">{t('filters.peopleCount', { count: filteredMemberIds.length })}</h2>
          )}
          {filter !== 'pets' && filteredMemberIds.length > 0 && (
            <div className="members-grid" data-tour="members-grid">
              {filteredMemberIds.map((memberId) => (
                <PersonCard
                  key={memberId}
                  familyMemberId={memberId}
                  generation={0}
                  layout="members"
                  onTreeViewClick={handleTreeViewClick}
                  onProfileClick={handleProfileClick}
                  onAddClick={handleAddClick}
                  canAddFamilyMember={canAddByMemberId[memberId] ?? false}
                  isTreeAnchor={false}
                />
              ))}
            </div>
          )}
          {filter === 'all' && filteredPetIds.length > 0 && (
            <h2 className="members-section-heading">{t('filters.petsCount', { count: filteredPetIds.length })}</h2>
          )}
          {filter !== 'people' && filteredPetIds.length > 0 && (
            <div
              className="members-grid"
              {...(filter === 'pets' || filteredMemberIds.length === 0 ? { 'data-tour': 'members-grid' } : {})}
            >
              {filteredPetIds.map((petId) => {
                const pet = familyStore.pets.get(petId);
                if (!pet) return null;
                return (
                  <PetCard key={petId} pet={pet} familyId={familyIdParam!} onProfileClick={handlePetProfileClick} />
                );
              })}
            </div>
          )}
        </>
      )}

      {showEditNameModal && familyIdParam && domainStore.currentFamily && (
        <EditFamilyNameModal
          familyId={familyIdParam}
          currentDisplayName={domainStore.currentFamily.displayName || domainStore.currentFamily.name}
          canonicalName={domainStore.currentFamily.name}
          hasAlternative={!!domainStore.currentFamily.alternativeName}
          isOpen={showEditNameModal}
          onClose={() => setShowEditNameModal(false)}
          onSuccess={handleEditNameSuccess}
        />
      )}

      <ScreenTourLayer tour={membersTour} />
    </div>
  );
});

export default FamilyMembersScreen;
